import Images from 'app/assets/img';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import { ReactComponent as BookingCodeIcon } from 'app/assets/img/icon_booking_code.svg';
import { ReactComponent as CalendarIcon } from 'app/assets/img/icon_calendar.svg';
import { ReactComponent as LocationIcon } from 'app/assets/img/icon_location_hotel.svg';
import { ReactComponent as PersonIcon } from 'app/assets/img/icon_person.svg';
import ModalContainer from 'app/components/ModalContainer';
import _ from 'lodash';
import moment from 'moment';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { renderReplaceText } from 'utils/helper';
import { selectTranslations } from '../App/slice/selectors';
import { formatDate } from '../BookingMask';
interface IDialogHotelDetailProps {
  hotels: any[];
  isShow: boolean;
  onCloseDialog: () => void;
  note?: string;
}
const FALLBACK_IMAGE = Images.ImgBanner;
const DialogHotelDetail: React.FC<IDialogHotelDetailProps> = props => {
  const { hotels, isShow, onCloseDialog, note = '' } = props;
  const translations = useSelector(selectTranslations);
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = FALLBACK_IMAGE;
  };
  return (
    <ModalContainer
      isShow={isShow}
      onCloseDialog={onCloseDialog}
      maskClosable={true}
      modalPadding="0"
      modalHeight="initial"
      styleSlots={{
        iconWrapperStyle: {
          margin: '0 16px 0 0',
        },
        modalContentStyle: {
          maxWidth: 980,
          width: '96vw',
          maxHeight: '96vh',
          height: 'auto',
        },
        modalBodyStyle: {
          height: 'fit-content',
        },
      }}
      modalProps={{ destroyOnClose: true }}
      CloseIcon={<CloseIcon width={12} height={12} />}
    >
      <TitleModel>
        {translations?.['b2b.txt.hotelInformation'] || ''}
      </TitleModel>
      <Line />
      <ModalBody>
        {hotels?.map(item => {
          const textPassenger = [
            item.adultQuantity &&
              `${item.adultQuantity} ${translations?.['b2b.txt.adult']}`,
            item.childQuantity &&
              `${item.childQuantity} ${translations?.['b2b.txt.child']}`,
            item.infantQuantity &&
              `${item.infantQuantity} ${translations?.['b2b.txt.infant']}`,
          ]
            .filter(Boolean)
            .join(', ');
          const firstRoomType = item?.detail?.roomTypes?.[0] || {};
          const displayTextCancelPolicies = [
            ...new Set(
              item?.detail?.ratePlans?.flatMap(
                (ratePlan: any) =>
                  ratePlan.cancellationPolicies?.flatMap(
                    (policy: any) => (policy.displayTexts || []) as string[],
                  ) || [],
              ) || [],
            ),
          ] as string[];
          return (
            <Content key={item.id || item.hotelId}>
              <Section>
                <SectionTitle>
                  {translations?.['b2b.txt.bookingInformation']}
                </SectionTitle>
                <SectionBody>
                  <InfoGrid>
                    <InfoRow>
                      <RowLeft>
                        <LocationIcon />
                        <Label>{translations?.['b2b.txt.hotel']}</Label>
                      </RowLeft>
                      <Value $bold>{item.hotelName}</Value>
                    </InfoRow>
                    <InfoRow>
                      <RowLeft>
                        <BookingCodeIcon />
                        <Label>
                          {translations?.['b2b.txt.bookingCodeRoom']}
                        </Label>
                      </RowLeft>
                      <Value>{item.bookingConfirmationNumber}</Value>
                    </InfoRow>
                    <InfoRow>
                      <RowLeft>
                        <CalendarIcon />
                        <Label>{translations?.['b2b.txt.checkInRoom']}</Label>
                      </RowLeft>
                      <Value>
                        {formatDate(moment(item.checkInDate), translations)}
                      </Value>
                    </InfoRow>
                    <InfoRow>
                      <RowLeft>
                        <CalendarIcon />
                        <Label>{translations?.['b2b.txt.checkOutRoom']}</Label>
                      </RowLeft>
                      <Value>
                        {formatDate(moment(item.checkOutDate), translations)}
                      </Value>
                    </InfoRow>
                    <InfoRow>
                      <RowLeft>
                        <PersonIcon />
                        <Label>{translations?.['b2b.txt.quantity']}</Label>
                      </RowLeft>
                      <Value>{textPassenger}</Value>
                    </InfoRow>
                  </InfoGrid>
                </SectionBody>
              </Section>
              <Section>
                <SectionTitle>
                  {translations?.['b2b.txt.roomInformation']}
                </SectionTitle>
                <SectionBody>
                  <RoomCard>
                    <RoomImage
                      src={
                        firstRoomType?.mediaFiles?.[0]?.mediaUrl ||
                        FALLBACK_IMAGE
                      }
                      onError={handleImageError}
                    />
                    <RoomContent>
                      <RoomName>{item.roomTypeName}</RoomName>
                      <RoomNote>
                        {renderReplaceText(
                          translations?.['b2b.txt.roomPackage'],
                          {
                            room: item.ratePlanName,
                          },
                        )}
                      </RoomNote>
                      {item.rateTags && (
                        <TagList>
                          {item.rateTags.split(',')?.map((tag, index) => {
                            return <Tag key={index}>{tag}</Tag>;
                          })}
                        </TagList>
                      )}
                    </RoomContent>
                    <Quantity>x{item.numberOfRoom}</Quantity>
                  </RoomCard>
                  {(item.extraBedCount > 0 || item.childSurchargeCount > 0) && (
                    <>
                      <Divider />
                      <SubTitle>
                        {translations?.['b2b.txt.additionalStay']}
                      </SubTitle>
                      <BulletList>
                        {item.extraBedCount > 0 && (
                          <li>
                            {renderReplaceText(
                              translations?.['b2b.txt.extraBed'],
                              {
                                bed: item.extraBedCount,
                              },
                            )}
                          </li>
                        )}
                        {item.childSurchargeCount > 0 && (
                          <li>
                            {renderReplaceText(
                              translations?.['b2b.txt.childSurcharge'],
                              {
                                child: item.childSurchargeCount,
                              },
                            )}
                          </li>
                        )}
                      </BulletList>
                    </>
                  )}
                  {!_.isEmpty(displayTextCancelPolicies) && (
                    <>
                      <Divider />
                      <CancelPolicyTitle>
                        {translations?.['b2b.txt.cancellationPolicy']}
                      </CancelPolicyTitle>
                      <BulletList>
                        {displayTextCancelPolicies.map((text, index) => (
                          <li key={index}>{text}</li>
                        ))}
                      </BulletList>
                    </>
                  )}
                </SectionBody>
              </Section>
              {note && (
                <Section>
                  <SectionTitle>
                    {translations?.['b2b.txt.additionalInformation']}
                  </SectionTitle>
                  <SectionBody>
                    <SubTitle>
                      {translations?.['b2b.txt.specialRequest']}
                    </SubTitle>
                    <RequestBox>{note}</RequestBox>
                  </SectionBody>
                </Section>
              )}
            </Content>
          );
        })}
      </ModalBody>
    </ModalContainer>
  );
};
export default DialogHotelDetail;
const TitleModel = styled.div`
  padding: 16px 0px 0px 12px;
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;
const ModalBody = styled.div`
  overflow-y: auto;
  flex: 1;
  max-height: calc(96vh - 70px);
`;
const Line = styled.div`
  height: 1px;
  background-color: #e5e5e5;
  margin-top: 12px;
`;
export const Content = styled.div`
  padding: 16px;
`;
export const Section = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  & + & {
    margin-top: 16px;
  }
`;
export const SectionTitle = styled.div`
  padding: 12px 16px;
  background: #f2f2f2;
  font-size: 14px;
  color: #000;
`;
export const SectionBody = styled.div`
  padding: 16px;
`;
export const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
export const InfoRow = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 12px;
  font-size: 14px;
`;
export const RowLeft = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;
export const Label = styled.div`
  color: #000;
  font-weight: 600;
`;
export const Value = styled.div<{ $bold?: boolean }>`
  color: #000;
  font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
`;
export const RoomCard = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
`;
export const RoomImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: cover;
  border-radius: 8px;
`;
export const RoomContent = styled.div`
  flex: 1;
`;
export const RoomName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;
export const RoomNote = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #777;
`;
export const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;
export const Tag = styled.span`
  padding: 4px 8px;
  font-size: 12px;
  background: #f3f3f3;
  border-radius: 4px;
`;
export const Quantity = styled.div`
  font-size: 14px;
  font-weight: 600;
`;
export const Divider = styled.div`
  margin: 16px 0;
  height: 1px;
  background: #e5e5e5;
`;
export const SubTitle = styled.div`
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 600;
`;
export const BulletList = styled.ul`
  margin: 0;
  padding-left: 18px;
  li {
    margin-bottom: 6px;
    font-size: 14px;
  }
`;
export const CancelPolicyTitle = styled.div`
  margin-bottom: 8px;

  color: #f06f24;
  font-size: 14px;
  font-weight: 600;
`;
export const RequestBox = styled.div`
  padding: 12px 16px;
  background: #f7f7f7;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
`;
